/**
 * InboxContext - Shared state for inbox/notification management
 * Provides real-time synchronization of inbox counts and tasks across components
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import apiService from '../services/api';

const InboxContext = createContext();

export const InboxProvider = ({ children }) => {
  const { currentUser, isAuthenticated } = useAuth();
  const [inboxCount, setInboxCount] = useState(0);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Load tasks from API
  const loadInboxData = useCallback(async () => {
    if (!isAuthenticated || !currentUser) {
      setInboxCount(0);
      setTasks([]);
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.getInboxTasks();
      const allTasks = response.tasks || [];

      // Set tasks
      setTasks(allTasks);

      // Count only pending tasks for the badge
      const pendingTasks = allTasks.filter(task => task.status === 'pending');
      setInboxCount(pendingTasks.length);

      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to load inbox data:', error);
      setInboxCount(0);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, currentUser]);

  // Initial load when user authenticates
  useEffect(() => {
    if (isAuthenticated && currentUser) {
      loadInboxData();

      // Set up polling to refresh every 30 seconds
      const pollInterval = setInterval(loadInboxData, 30000);
      return () => clearInterval(pollInterval);
    } else {
      setInboxCount(0);
      setTasks([]);
    }
  }, [isAuthenticated, currentUser, loadInboxData]);

  // Manually trigger refresh (called after completing/dismissing tasks)
  const refreshInbox = useCallback(async () => {
    await loadInboxData();
  }, [loadInboxData]);

  const value = {
    inboxCount,
    setInboxCount,
    tasks,
    setTasks,
    loading,
    lastRefresh,
    loadInboxData,
    refreshInbox
  };

  return (
    <InboxContext.Provider value={value}>
      {children}
    </InboxContext.Provider>
  );
};

export const useInbox = () => {
  const context = useContext(InboxContext);
  if (!context) {
    throw new Error('useInbox must be used within InboxProvider');
  }
  return context;
};
